'use client';

import { useEffect } from 'react';

/**
 * CTA tagger (owner GO 2026-10-04): tells the lead email WHICH button or section a lead clicked. When someone clicks a
 * link to the demo form or /pricing, it adds cta=<slug> to that link at click time, so page HTML, SEO and the parity
 * gates never change. The form sends the URL with the submit and LP SEO PROJECT prints it in the lead email
 * (param name agreed with them: cta, lowercase slugs, no page in it; the page comes from the referrer).
 *
 * Slug, first match wins: data-cta on the button or an ancestor · nav (site header / menus) · footer · sticky (the
 * v2 sticky bar) · the id of the section it sits in · the nearest H2 above it · hero (above the first H2).
 * Skips links that already carry cta or xp (exit popup buttons keep their own xp id) and clicks on the form itself.
 * A Next <Link> navigates by its own href prop and ignores the edited href, but it runs its onClick and then skips
 * navigation when the click is already defaultPrevented. So for a plain left click we cancel the default and go to the
 * tagged URL ourselves; every other click handler (tracking) still runs. New-tab clicks just get the edited href.
 */
const slug = (s: string) => {
  const t = s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return t.length <= 40 ? t : t.slice(0, t.lastIndexOf('-', 40) > 10 ? t.lastIndexOf('-', 40) : 40); // whole words
};
const GENERIC_ID = /^(__next|main|root|content|radix|headlessui|:r)/i;
const isForm = (p: string) => p === '/pricing' || p.startsWith('/mortgage-lead-generation-demo');

function sectionOf(a: HTMLAnchorElement): string {
  const tagged = a.closest('[data-cta]')?.getAttribute('data-cta');
  if (tagged) return slug(tagged);
  const header = a.closest('header, [role="banner"]');
  if ((header && !header.closest('main, article, .lp-root')) || a.closest('.mega-menu-fallback, .lp-mm')) return 'nav'; // site menu, not a hero <header>
  if (a.closest('footer')) return 'footer';
  if (a.closest('.lp-sticky-in')) return 'sticky';
  for (let el: Element | null = a; el && el !== document.body; el = el.parentElement) {
    if (getComputedStyle(el).position === 'fixed') return 'floating'; // a floating button pinned to the screen
  }
  for (let el = a.parentElement; el && el !== document.body; el = el.parentElement) {
    if (el.id && !GENERIC_ID.test(el.id)) return slug(el.id.replace(/^lp-/, '')); // lp-hero -> hero
  }
  let heading: Element | null = null;
  for (const h of Array.from(document.querySelectorAll('h2'))) {
    if (h.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_FOLLOWING) heading = h; else break;
  }
  return heading?.textContent ? slug(heading.textContent) || 'hero' : 'hero';
}

export function CtaTagger() {
  useEffect(() => {
    const tag = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || isForm(window.location.pathname)) return;
      let url: URL;
      try { url = new URL(a.href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin || !isForm(url.pathname)) return;
      if (url.searchParams.has('cta') || url.searchParams.has('xp')) return;
      url.searchParams.set('cta', sectionOf(a));
      const to = url.pathname + url.search + url.hash;
      a.href = to;
      const plain = e.type === 'click' && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
        && (!a.target || a.target === '_self') && !a.hasAttribute('download');
      if (plain && !e.defaultPrevented) { e.preventDefault(); window.location.assign(to); }
    };
    document.addEventListener('click', tag, true);
    document.addEventListener('auxclick', tag, true);
    return () => { document.removeEventListener('click', tag, true); document.removeEventListener('auxclick', tag, true); };
  }, []);
  return null;
}
